import styled from 'styled-components';

export const Container = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
    width: 100%;
    padding: 0;
`;

export const Header = styled.div`
    display: flex;
    align-items: center;
    margin-bottom: 0;

    p {
        font-size: 14px;
        font-weight: 400;
        color: ${({ theme }) => theme.colors.interfaceText300};
        margin: 0;
    }
`;

export const BoardContainer = styled.div`
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 16px;

    @media (max-width: 1024px) {
        grid-template-columns: repeat(2, 1fr);
    }

    @media (max-width: 768px) {
        grid-template-columns: 1fr;
        gap: 12px;
    }
`;

export const Coluna = styled.div<{ isOver?: boolean; bgColor: string }>`
    display: flex;
    flex-direction: column;
    background: rgba(210, 214, 219, 0.2);
    border-radius: 6px;
    min-height: 200px;
    ${({ isOver, theme }) => isOver && `
        border: 2px dashed ${theme.colors.interfaceText300};
    `}
`;

export const ColunaHeader = styled.div<{ bgColor: string }>`
    padding: 12px;
    border-bottom: 1px solid ${({ theme }) => theme.colors.interfaceText100};
    border-radius: 6px 6px 0 0;
    background-color: ${({ bgColor }) => bgColor};
    flex-shrink: 0;
    min-height: 52px;
    display: flex;
    align-items: center;

    .header-content {
        display: flex;
        align-items: center;
        justify-content: space-between;
        flex-wrap: wrap;
        gap: 8px;
        width: 100%;

        .title-group {
            display: flex;
            align-items: center;
            gap: 8px;
            flex-wrap: wrap;
            flex: 1;
            min-width: 0;

            h3 {
                font-size: 14px;
                font-weight: 500;
                color: ${({ theme }) => theme.colors.interfaceText400};
                margin: 0;
                white-space: nowrap;
            }
        }

        .badge {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            width: 27px;
            min-width: 16px;
            line-height: 18px;
            padding: 4px;
            font-size: 11px;
            font-weight: 600;
            background-color: var(--kanban-badge-bg);
            color: var(--count-badge-ink);
            border: 1px solid var(--kanban-badge-border);
            border-radius: 4px;
            text-align: center;
            flex-shrink: 0;
        }
    }
`;

export const FiltrosResultado = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin: 0;
    padding: 0;
    align-items: center;

    button {
        padding: 4px 8px;
        border-radius: 6px;
        font-size: 12px;
        font-weight: 500;
        cursor: pointer;
        transition: all 0.2s ease;
        box-sizing: border-box;

        &.resolvida {
            background: ${({ theme }) => theme.colors.interfaceSuccess100};
            color: ${({ theme }) => theme.colors.interfaceSuccess400};
            border: 1px solid ${(props: any) => props.theme.colors.interfaceSuccess200};
            
            &.ativo {
                background: ${({ theme }) => theme.colors.interfaceSuccess200};
                border: 1px solid ${({ theme }) => theme.colors.interfaceSuccess400};
                font-weight: 600;
            }
        }

        &.aceita {
            background: ${({ theme }) => theme.colors.secondaryPool100};
            color: ${({ theme }) => theme.colors.primaryBlue100};
            border: 1px solid ${(props: any) => props.theme.colors.secondaryPool200};
            
            &.ativo {
                background: ${({ theme }) => theme.colors.secondaryPool200};
                border: 1px solid ${({ theme }) => theme.colors.primaryBlue100};
                font-weight: 600;
            }
        }

        &.recusada {
            background: ${({ theme }) => theme.colors.interfaceDanger100};
            color: ${({ theme }) => theme.colors.interfaceDanger400};
            border: 1px solid ${(props: any) => props.theme.colors.interfaceDanger200};
            
            &.ativo {
                background: ${({ theme }) => theme.colors.interfaceDanger200};
                border: 1px solid ${({ theme }) => theme.colors.interfaceDanger400};
                font-weight: 600;
            }
        }
    }
`;

export const DropZone = styled.div<{ isOver?: boolean }>`
    flex: 1 1 auto;
    padding: 8px;
    display: flex;
    flex-direction: column;
    gap: 8px;
    background: transparent;
    pointer-events: auto;
    
    ${({ isOver, theme }) => isOver && `
        background: rgba(234, 238, 243, 0.3);
        border: 2px dashed ${theme.colors.interfaceText300};
        border-radius: 6px;
    `}
`;

export const EmptyState = styled.div<{ isOver?: boolean }>`
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 32px 16px;
    text-align: center;
    border: 2px dashed ${({ theme, isOver }) => 
        isOver 
            ? theme.colors.interfaceText300 
            : theme.colors.interfaceText200
    };
    border-radius: 6px;
    background: ${({ theme, isOver }) => 
        isOver 
            ? theme.colors.interfaceText100 
            : 'transparent'
    };
    color: ${({ theme, isOver }) => 
        isOver 
            ? theme.colors.interfaceText400 
            : theme.colors.interfaceText300
    };
    font-size: 12px;
    font-weight: 500;
    margin: 8px;
`;

export const Card = styled.div<{ isDragging?: boolean; canMove?: boolean }>`
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 14px;
    background: ${({ theme }) => theme.colors.interfacePure0};
    border: 1px solid ${({ theme }) => theme.colors.interfaceText100};
    border-radius: 6px;
    cursor: ${({ canMove }) => canMove ? 'grab' : 'pointer'};
    transition: all 0.2s ease;
    opacity: ${({ isDragging }) => isDragging ? 0.5 : 1};
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
    touch-action: none;
    -webkit-touch-callout: none;
    -webkit-user-select: none;
    user-select: none;

    &.dragging {
        cursor: grabbing !important;
        * {
            cursor: grabbing !important;
        }
    }

    ${({ canMove, theme }) => canMove && `
        &:hover {
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
            border-color: ${theme.colors.interfaceText200};
            
            .grip-icon {
                opacity: 1 !important;
            }
        }

        &:active {
            cursor: grabbing !important;
        }
    `}

    .card-header {
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        gap: 8px;

        .badges {
            display: flex;
            flex-wrap: wrap;
            gap: 6px;
        }

        .header-right {
            display: flex;
            align-items: center;
            gap: 8px;
            flex-shrink: 0;
        }

        .pendencia-id {
            font-size: 11px;
            color: ${({ theme }) => theme.colors.interfaceText300};
            font-family: monospace;
            font-weight: 500;
            user-select: text;
        }

        .icons {
            display: flex;
            align-items: center;
            gap: 6px;
            flex-shrink: 0;
        }
    }

    .card-content {
        p {
            font-size: 14px;
            font-weight: 400;
            line-height: 20px;
            color: ${({ theme }) => theme.colors.interfaceText400};
            margin: 0;
            display: -webkit-box;
            -webkit-line-clamp: 4;
            -webkit-box-orient: vertical;
            overflow: hidden;
            word-break: break-word;
            overflow-wrap: break-word;
            text-overflow: ellipsis;
        }
    }

    .card-footer {
        display: flex;
        align-items: center;
        justify-content: space-between;
        flex-wrap: wrap;
        gap: 8px;

        .footer-left {
            display: flex;
            align-items: center;
            gap: 6px;
            flex-wrap: wrap;
            flex: 1;
            min-width: 0;

            .criador-nome {
                font-size: 12px;
                color: ${({ theme }) => theme.colors.interfaceText400};
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
                max-width: 120px;
            }
        }

        .time {
            display: flex;
            align-items: center;
            gap: 4px;
            font-size: 12px;
            color: ${({ theme }) => theme.colors.interfaceText300};
            flex-shrink: 0;
        }
    }
`;

export const TimeContainer = styled.div`
    position: relative;
    display: inline-flex;
    align-items: center;
    cursor: help;

    &:hover > div:last-child {
        opacity: 1;
        visibility: visible;
    }
`;

export const Tooltip = styled.div`
    position: absolute;
    bottom: calc(100% + 8px);
    left: 50%;
    transform: translateX(-50%);
    background: ${({ theme }) => theme.colors.interfaceGray700 || '#333'};
    color: ${({ theme }) => theme.colors.interfacePure0 || '#fff'};
    font-size: 12px;
    font-weight: 500;
    padding: 6px 10px;
    border-radius: 6px;
    white-space: nowrap;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.18);
    z-index: 9999;
    pointer-events: none;
    opacity: 0;
    visibility: hidden;
    transition: opacity 0.2s ease, visibility 0.2s ease;

    &::after {
        content: '';
        position: absolute;
        top: 100%;
        left: 50%;
        transform: translateX(-50%);
        border-width: 5px;
        border-style: solid;
        border-color: ${({ theme }) => theme.colors.interfaceGray700 || '#333'} transparent transparent transparent;
    }
`;

export const Badge = styled.span<{ variant?: 'tipo' | 'resolvida' | 'aceita' | 'recusada' }>`
    display: inline-flex;
    align-items: center;
    padding: 4px 8px;
    border-radius: 6px;
    font-size: 12px;
    font-weight: 500;
    border: 1px solid;
    width: fit-content;

    ${({ variant, theme }) => {
        switch (variant) {
            case 'resolvida':
                return `
                    background: ${theme.colors.interfaceSuccess100};
                    color: ${theme.colors.interfaceSuccess400};
                    border-color: ${theme.colors.interfaceSuccess200};
                `;
            case 'aceita':
                return `
                    background: ${theme.colors.secondaryPool100};
                    color: ${theme.colors.primaryBlue100};
                    border-color: ${theme.colors.secondaryPool200};
                `;
            case 'recusada':
                return `
                    background: ${theme.colors.interfaceDanger100};
                    color: ${theme.colors.interfaceDanger400};
                    border-color: ${theme.colors.interfaceDanger200};
                `;
            default:
                return `
                    background: ${theme.colors.interfaceText100};
                    color: ${theme.colors.interfaceText400};
                    border-color: ${theme.colors.interfaceText200};
                `;
        }
    }}
`;

export const ButtonNovaPendencia = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    height: 40px;
    padding: 0 1.25rem;
    border: 2px solid ${({ theme }) => theme.colors.primaryRed400};
    border-radius: 6px;
    background: ${({ theme }) => theme.colors.primaryRed400};
    color: ${({ theme }) => theme.colors.pureWhite};
    font-size: 0.875rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
    white-space: nowrap;
    flex-shrink: 0;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);

    &:hover {
        transform: scale(1.02);
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);
    }

    &:active {
        transform: translateY(1px);
    }

    svg {
        width: 16px;
        height: 16px;
    }
`;


